import AsyncLock from 'async-lock';
import ky from 'ky';
import loki from 'lokijs';
import Config, { type BrainAtlasType, BrainAtlasTypes } from '../../config';
import Functions from '../../functions';
import simpfLinksJson from '../assets/simpf-links.json';
import AdvancedSearchQuery from './AdvancedSearchQuery';
import type { LokiSearchFilter } from './types';

interface SimPFLink {
    id: number;
    url: string;
}

interface ItemSubType<T> {
    type: T;
    label: string;
}

export type ItemSubTypes<T> = readonly ItemSubType<T>[];

export type ItemBasicLang =
    | 'eng'
    | 'jpn'
    | 'fra'
    | 'deu'
    | 'esl'
    | 'ita'
    | 'dut'
    | 'sve'
    | 'nor'
    | 'dan'
    | 'fin'
    | 'por'
    | 'chi'
    | 'kor';
export interface ItemBasicIndex {
    index_id: number;
    title: string;
}
export interface ItemBasicChangeLog {
    log_date: number;
    log: string;
}
export interface ItemBasicFile {
    file_id: number;
    original_file_name: string;
    mime_type: string;
    file_size: number;
    caption: string;
    timestamp: string;
    file_type_name: string;
    file_type_display_name: string;
}
export interface ItemCore {
    item_id: number;
    doi: string;
}
export interface ItemBasic extends ItemCore {
    uid: number;
    description: string;
    last_update_date: number;
    creation_date: number;
    publication_year: number;
    publication_month: number;
    publication_mday: number;
    lang: ItemBasicLang;
    title: string;
    item_type_display_name: string;
    item_type_name: string;
    uname: string;
    name: string;
    item_url: string;
    index: ItemBasicIndex[];
    changelog: ItemBasicChangeLog[];
    related_to: number[];
    keyword: string[];
    file: ItemBasicFile[];
}

export interface ItemBinder extends ItemBasic {
    extra: string;
    item_link: number[];
}

export interface ItemBook extends ItemBasic {
    classification: string;
    editor: string;
    publisher: string;
    isbn: string;
    url: string;
    attachment_dl_limit: number;
    attachment_dl_notify: number;
    author: string[];
}

export type ItemConferenceSubType = 'powerpoint' | 'pdf' | 'illustrator' | 'other';
export const ItemConferenceSubTypes: ItemSubTypes<ItemConferenceSubType> = [
    { type: 'powerpoint', label: 'PowerPoint' },
    { type: 'pdf', label: 'PDF' },
    { type: 'illustrator', label: 'Illustrator' },
    { type: 'other', label: 'Other' },
];
export interface ItemConference extends ItemBasic {
    presentation_type: ItemConferenceSubType;
    conference_title: string;
    place: string;
    abstract: string;
    conference_from_year: number;
    conference_from_month: number;
    conference_from_mday: number;
    conference_to_year: number;
    conference_to_month: number;
    conference_to_mday: number;
    attachment_dl_limit: number;
    attachment_dl_notify: number;
    author: string[];
}

export type ItemDataSubType = 'excel' | 'movie' | 'text' | 'picture' | 'other';
export const ItemDataSubTypes: ItemSubTypes<ItemDataSubType> = [
    { type: 'excel', label: 'Excel' },
    { type: 'movie', label: 'Movie' },
    { type: 'text', label: 'Text' },
    { type: 'picture', label: 'Picture' },
    { type: 'other', label: 'Other' },
];
export interface ItemData extends ItemBasic {
    data_type: ItemDataSubType;
    rights: string;
    readme: string;
    use_cc: number;
    cc_commercial_use: number;
    cc_modification: number;
    attachment_dl_limit: number;
    attachment_dl_notify: number;
    experimenter: string[];
}

export type ItemFilesSubType = 'pdf' | 'zip'; // Brain Atlas extended
export const ItemFilesSubTypes: ItemSubTypes<ItemFilesSubType> = [
    { type: 'pdf', label: 'pdf' },
    { type: 'zip', label: 'zip' },
];
export interface ItemFiles extends ItemBasic {
    data_file_name: string;
    data_file_mimetype: string;
    data_file_filetype: string;
}

export interface ItemMemo extends ItemBasic {
    item_link: string;
}

export type ItemModelSubType = 'matlab' | 'neuron' | 'original_program' | 'satellite' | 'genesis' | 'a_cell' | 'other';
export const ItemModelSubTypes: ItemSubTypes<ItemModelSubType> = [
    { type: 'matlab', label: 'Matlab' },
    { type: 'neuron', label: 'Neuron' },
    { type: 'original_program', label: 'Original Program' },
    { type: 'satellite', label: 'Satellite' },
    { type: 'genesis', label: 'Genesis' },
    { type: 'a_cell', label: 'A-Cell' },
    { type: 'other', label: 'Other' },
];
export interface ItemModel extends ItemBasic {
    model_type: ItemModelSubType;
    readme: string;
    rights: string;
    use_cc: number;
    cc_commercial_use: number;
    cc_modification: number;
    attachment_dl_limit: number;
    attachment_dl_notify: number;
    creator: string[];
}

export interface ItemPaper extends ItemBasic {
    journal: string;
    volume: number;
    number: number | null;
    page: string;
    abstract: string;
    pubmed_id: string;
    author: string[];
}

export type ItemPresentationSubType = 'powerpoint' | 'lotus' | 'justsystem' | 'html' | 'pdf' | 'other';
export const ItemPresentationSubTypes: ItemSubTypes<ItemPresentationSubType> = [
    { type: 'powerpoint', label: 'PowerPoint' },
    { type: 'lotus', label: 'Lotus' },
    { type: 'justsystem', label: 'JustSystem' },
    { type: 'html', label: 'HTML' },
    { type: 'pdf', label: 'PDF' },
    { type: 'other', label: 'Other' },
];
export interface ItemPresentation extends ItemBasic {
    presentation_type: ItemPresentationSubType;
    use_cc: number;
    cc_commercial_use: number;
    cc_modification: number;
    rights: string;
    readme: string;
    attachment_dl_limit: number;
    attachment_dl_notify: number;
    creator: string[];
}

export type ItemSimulatorSubType = 'matlab' | 'mathematica' | 'program' | 'other';
export const ItemSimulatorSubTypes: ItemSubTypes<ItemSimulatorSubType> = [
    { type: 'matlab', label: 'Matlab' },
    { type: 'mathematica', label: 'Mathematica' },
    { type: 'program', label: 'Program' },
    { type: 'other', label: 'Other' },
];
export interface ItemSimulator extends ItemBasic {
    simulator_type: ItemSimulatorSubType;
    readme: string;
    rights: string;
    use_cc: number;
    cc_commercial_use: number;
    cc_modification: number;
    attachment_dl_limit: number;
    attachment_dl_notify: number;
    developer: string[];
}

export type ItemStimulusSubType = 'picture' | 'movie' | 'program' | 'other';
export const ItemStimulusSubTypes: ItemSubTypes<ItemStimulusSubType> = [
    { type: 'picture', label: 'Picture' },
    { type: 'movie', label: 'Movie' },
    { type: 'program', label: 'Program' },
    { type: 'other', label: 'Other' },
];
export interface ItemStimulus extends ItemBasic {
    stimulus_type: ItemStimulusSubType;
    readme: string;
    rights: string;
    use_cc: number;
    cc_commercial_use: number;
    cc_modification: number;
    attachment_dl_limit: number;
    attachment_dl_notify: number;
    developer: string[];
}

export type ItemToolSubType = 'matlab' | 'mathematica' | 'program' | 'other';
export const ItemToolSubTypes: ItemSubTypes<ItemToolSubType> = [
    { type: 'matlab', label: 'Matlab' },
    { type: 'mathematica', label: 'Mathematica' },
    { type: 'program', label: 'Program' },
    { type: 'other', label: 'Other' },
];
export interface ItemTool extends ItemBasic {
    tool_type: ItemToolSubType;
    readme: string;
    rights: string;
    use_cc: number;
    cc_commercial_use: number;
    cc_modification: number;
    attachment_dl_limit: number;
    attachment_dl_notify: number;
    developer: string[];
}

export interface ItemUrl extends ItemBasic {
    url: string;
    url_count: number;
}

export type Item =
    | ItemBinder
    | ItemBook
    | ItemConference
    | ItemData
    | ItemFiles
    | ItemMemo
    | ItemModel
    | ItemPaper
    | ItemPresentation
    | ItemSimulator
    | ItemStimulus
    | ItemTool
    | ItemUrl;

export type ItemSubTypesAll =
    | ItemSubTypes<ItemConferenceSubType>
    | ItemSubTypes<ItemDataSubType>
    | ItemSubTypes<ItemFilesSubType> // Brain Atlas extended
    | ItemSubTypes<ItemModelSubType>
    | ItemSubTypes<ItemPresentationSubType>
    | ItemSubTypes<ItemSimulatorSubType>
    | ItemSubTypes<ItemStimulusSubType>
    | ItemSubTypes<ItemToolSubType>;

export type KeywordSearchType =
    | 'all'
    | 'basic'
    | 'binder'
    | 'book'
    | 'conference'
    | 'data'
    | 'files'
    | 'memo'
    | 'model'
    | 'paper'
    | 'presentation'
    | 'simulator'
    | 'stimulus'
    | 'tool'
    | 'url';
export interface KeywordSearchQuery {
    type: KeywordSearchType;
    keyword: string;
}
const KEYWORD_SEARCH_TYPE_RANGE = [
    'all',
    'basic',
    'binder',
    'book',
    'conference',
    'files',
    'data',
    'memo',
    'model',
    'paper',
    'presentation',
    'simulator',
    'stimulus',
    'tool',
    'url',
];

export type SortConditionLimit = 20 | 50 | 100;
export type SortConditionOrderBy = 'title' | 'doi' | 'last_update_date' | 'creation_date' | 'publication_date';
export enum SortConditionOrderDir {
    ASC,
    DESC,
}

export interface SortCondition {
    limit: SortConditionLimit;
    orderBy: SortConditionOrderBy;
    orderDir: SortConditionOrderDir;
    page: number;
}

class ItemSorter {
    public orderBy: SortConditionOrderBy;
    public orderDir: SortConditionOrderDir;

    constructor(condition: SortCondition) {
        this.orderBy = condition.orderBy;
        this.orderDir = condition.orderDir;
        this.sort = this.sort.bind(this);
    }

    sort(a: Item, b: Item): number {
        let av = '';
        let bv = '';
        switch (this.orderBy) {
            case 'title':
                av = a.title.toLocaleUpperCase();
                bv = b.title.toLocaleUpperCase();
                break;
            case 'doi':
                av = a.doi.toLocaleUpperCase();
                bv = b.doi.toLocaleUpperCase();
                break;
            case 'last_update_date':
                av = String(a.last_update_date);
                bv = String(b.last_update_date);
                break;
            case 'creation_date':
                av = String(a.creation_date);
                bv = String(b.creation_date);
                break;
            case 'publication_date':
                av = String(a.publication_year * 10000 + a.publication_month * 100 + a.publication_mday);
                bv = String(b.publication_year * 10000 + b.publication_month * 100 + b.publication_mday);
                break;
            default:
                break;
        }
        if (this.orderDir === SortConditionOrderDir.ASC) {
            if (av > bv) return -1;
            else if (av < bv) return 1;
        } else {
            if (av > bv) return 1;
            else if (av < bv) return -1;
        }
        return 0;
    }
}

type GetResult = Item | null;
export type GetCallbackFunc = (item: GetResult) => void;
export interface SearchResult {
    total: number;
    data: Item[];
}
export type SearchCallbackFunc = (results: SearchResult) => void;
export type SearchFunc = (condition: SortCondition, func: SearchCallbackFunc) => void;

type ItemLoadCallbackFunc = (items: Collection<Item>) => void;

class ItemUtil {
    private database: loki;
    private items: Map<BrainAtlasType, Collection<Item>>;
    private simpfLinks: Collection<SimPFLink>;
    private loading: Map<BrainAtlasType, boolean>;
    private callbacks: Map<BrainAtlasType, ItemLoadCallbackFunc[]>;

    constructor() {
        this.database = new loki('database', { env: 'BROWSER' });
        this.items = new Map<BrainAtlasType, Collection<Item>>();
        this.simpfLinks = this.database.addCollection('simpf-links');
        this.loading = new Map<BrainAtlasType, boolean>();
        this.callbacks = new Map<BrainAtlasType, ItemLoadCallbackFunc[]>();
        this.load();
    }

    load(): void {
        BrainAtlasTypes.forEach((type) => {
            const collection = this.database.addCollection<Item>(`${type}_items`);
            this.items.set(type, collection);
            this.loading.set(type, true);
            this.callbacks.set(type, []);
            ky.get(new URL(`${Config.BASE_URL}/${type}/modules/xoonips/items.json`, window.location.href))
                .json<Item[]>()
                .then((data) => {
                    const itemsJson = data;
                    itemsJson.forEach((json: Item) => {
                        collection.insert(json);
                    });
                    const lock = new AsyncLock();
                    lock.acquire(
                        `${type}_items`,
                        () => {
                            this.loading.set(type, false);
                            const callbacks = this.callbacks.get(type);
                            if (typeof callbacks !== 'undefined') {
                                callbacks.forEach((callback) => {
                                    callback(collection);
                                });
                                this.callbacks.set(type, []);
                            }
                        },
                        () => {
                            // ignore
                        }
                    );
                })
                .catch(() => {
                    // ignore
                });
        });
        const simpfLinks = simpfLinksJson as SimPFLink[];
        simpfLinks.forEach((simpfLink: SimPFLink) => {
            this.simpfLinks.insert(simpfLink);
        });
    }

    registerItemLoadCallback(type: BrainAtlasType, func: ItemLoadCallbackFunc): void {
        const lock = new AsyncLock();
        lock.acquire(
            `${type}_items`,
            () => {
                if (this.loading.get(type)) {
                    const callbacks = this.callbacks.get(type);
                    if (typeof callbacks !== 'undefined') {
                        callbacks.push(func);
                    }
                } else {
                    const collection = this.items.get(type);
                    if (typeof collection !== 'undefined') {
                        func(collection);
                    }
                }
            },
            () => {
                // ignore
            }
        );
    }

    getUrl(type: BrainAtlasType, item: ItemCore): string {
        const url = `/${type}/modules/xoonips/detail.php`;
        if (item.doi !== '') {
            return `${url}?id=${Functions.escapeUrl(item.doi)}`;
        }
        return `${url}?item_id=${String(item.item_id)}`;
    }

    getFileUrl(type: BrainAtlasType, file: ItemBasicFile): string {
        return `${Config.BASE_URL}/${type}/modules/xoonips/file/${file.file_id}/${Functions.escapeUrl(file.original_file_name)}`;
    }

    getPreviewFileUrl(type: BrainAtlasType, file: ItemBasicFile): string {
        return `${Config.BASE_URL}/${type}/modules/xoonips/file/${file.file_id}.png`;
    }

    getSearchByKeywordUrl(type: BrainAtlasType, ksType: KeywordSearchType, keyword: string): string {
        const params = new URLSearchParams({ keyword, search_itemtype: ksType });
        return `/${type}/modules/xoonips/itemselect.php?op=quicksearch&${params.toString()}`;
    }

    getItemTypeSearchUrl(type: BrainAtlasType, itemType: string, subItemType: string): string {
        const url = `/${type}/modules/xoonips/itemselect.php`;
        if (subItemType !== '') {
            return (
                url +
                '?op=itemsubtypesearch&search_itemtype=xnp' +
                Functions.escapeUrl(itemType) +
                '&search_subitemtype=' +
                Functions.escapeUrl(subItemType)
            );
        }
        return `${url}?op=itemtypesearch&search_itemtype=xnp${Functions.escapeUrl(itemType)}`;
    }

    getSearchByAdvancedKeywordsUrl(type: BrainAtlasType, query: AdvancedSearchQuery): string {
        const paramString = query.getQueryParams().toString();
        return `/${type}/modules/xoonips/itemselect.php?op=advanced${paramString.length > 0 ? `&${paramString}` : ''}`;
    }

    getSearchKeywordByQuery(queryString: string): KeywordSearchQuery {
        const query = new URLSearchParams(queryString);
        const qType = query.get('search_itemtype');
        const type = qType !== null && KEYWORD_SEARCH_TYPE_RANGE.includes(qType) ? (qType as KeywordSearchType) : 'all';
        const keyword = query.get('keyword') ?? '';
        return { type, keyword };
    }

    getAdvancedSearchQueryByQuery(queryString: string): AdvancedSearchQuery {
        const query: AdvancedSearchQuery = new AdvancedSearchQuery();
        query.setByQueryString(queryString);
        return query;
    }

    get(type: BrainAtlasType, itemId: number, func: GetCallbackFunc): void {
        this.registerItemLoadCallback(type, (items) => {
            const filter: LokiSearchFilter = {
                item_id: itemId,
            };
            const item = items.findOne(filter);
            func(item);
        });
    }

    getByDoi(type: BrainAtlasType, doi: string, func: GetCallbackFunc): void {
        this.registerItemLoadCallback(type, (items) => {
            const filter: LokiSearchFilter = {
                doi: doi,
            };
            const item = items.findOne(filter);
            func(item);
        });
    }

    getList(type: BrainAtlasType, itemIds: number[], func: SearchCallbackFunc): void {
        this.registerItemLoadCallback(type, (items) => {
            const filter: LokiSearchFilter = {
                item_id: {
                    $in: itemIds,
                },
            };
            const sort = (a: Item, b: Item) => {
                const aIdx = itemIds.indexOf(a.item_id);
                const bIdx = itemIds.indexOf(b.item_id);
                if (aIdx > bIdx) {
                    return 1;
                } else if (aIdx < bIdx) {
                    return -1;
                }
                return 0;
            };
            const data = items.chain().find(filter).sort(sort).data();
            const res = {
                total: data.length,
                data: data,
            };
            func(res);
        });
    }

    getListByIndexId(type: BrainAtlasType, indexId: number, condition: SortCondition, func: SearchCallbackFunc): void {
        this.registerItemLoadCallback(type, (items) => {
            const filter: LokiSearchFilter = {
                'index.index_id': indexId,
            };
            const offset = condition.limit * (condition.page - 1);
            const result = items.chain().find(filter);
            const itemSorter = new ItemSorter(condition);
            const ret = {
                total: result.count(),
                data: result
                    .sort((a, b) => itemSorter.sort(a, b))
                    .offset(offset)
                    .limit(condition.limit)
                    .data(),
            };
            func(ret);
        });
    }

    getListByItemType(
        type: BrainAtlasType,
        itemType: string,
        subItemType: string,
        condition: SortCondition,
        func: SearchCallbackFunc
    ): void {
        this.registerItemLoadCallback(type, (items) => {
            const filter: LokiSearchFilter = {
                item_type_name: `xnp${itemType}`,
            };
            if (subItemType !== '') {
                switch (itemType) {
                    case 'conference':
                        filter.presentation_type = subItemType;
                        break;
                    case 'data':
                        filter.data_type = subItemType;
                        break;
                    case 'files':
                        filter.data_file_filetype = subItemType;
                        break;
                    case 'model':
                        filter.model_type = subItemType;
                        break;
                    case 'presentation':
                        filter.presentation_type = subItemType;
                        break;
                    case 'simulator':
                        filter.simulator_type = subItemType;
                        break;
                    case 'stimulus':
                        filter.stimulus_type = subItemType;
                        break;
                    case 'tool':
                        filter.tool_type = subItemType;
                        break;
                    default:
                        break;
                }
            }
            const offset = condition.limit * (condition.page - 1);
            const result = items.chain().find(filter);
            const itemSorter = new ItemSorter(condition);
            const ret = {
                total: result.count(),
                data: result
                    .sort((a, b) => itemSorter.sort(a, b))
                    .offset(offset)
                    .limit(condition.limit)
                    .data(),
            };
            func(ret);
        });
    }

    getListByKeyword(
        type: BrainAtlasType,
        ksType: KeywordSearchType,
        keyword: string,
        condition: SortCondition,
        func: SearchCallbackFunc
    ): void {
        this.registerItemLoadCallback(type, (items) => {
            const regex = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const num = keyword.match(/^[0-9]+$/) ? parseInt(keyword, 10) : null;
            const filter: LokiSearchFilter = {};
            const filterOr: LokiSearchFilter[] = [];
            const appendToFilter = (type: string, strKeys: string[], numKeys: string[]) => {
                const basicStrKeys: string[] = [
                    'title',
                    'keyword',
                    'doi',
                    'description',
                    'uname',
                    'name',
                    'index.title',
                ];
                const basicNumKeys: string[] = [];
                const filterItemTypeOr: LokiSearchFilter[] = [];
                const sKeys: string[] = basicStrKeys.concat(strKeys);
                const nKeys: string[] = basicNumKeys.concat(numKeys);
                sKeys.forEach((key) => {
                    const criteria: LokiSearchFilter = {};
                    criteria[key] = { $regex: [regex, 'i'] };
                    filterItemTypeOr.push(criteria);
                });
                if (num !== null) {
                    nKeys.forEach((key) => {
                        const criteria: LokiSearchFilter = {};
                        criteria[key] = { $eq: num };
                        filterItemTypeOr.push(criteria);
                    });
                }
                filterOr.push({
                    item_type_name: `xnp${type}`,
                    $or: filterItemTypeOr,
                });
            };
            if (ksType === 'basic') {
                filterOr.push({ title: { $regex: [regex, 'i'] } });
                filterOr.push({ keyword: { $regex: [regex, 'i'] } });
            } else {
                if (ksType === 'all' || ksType === 'binder') {
                    const strKeys: string[] = ['doi', 'title', 'keyword', 'description'];
                    const numKeys: string[] = [];
                    appendToFilter('binder', strKeys, numKeys);
                }
                if (ksType === 'all' || ksType === 'book') {
                    const strKeys: string[] = [
                        'doi',
                        'title',
                        'keyword',
                        'classification',
                        'editor',
                        'publisher',
                        'isbn',
                        'url',
                        'author',
                        'file.caption',
                        'file.original_file_name',
                    ];
                    const numKeys: string[] = [];
                    appendToFilter('book', strKeys, numKeys);
                }
                if (ksType === 'all' || ksType === 'conference') {
                    const strKeys: string[] = [
                        'doi',
                        'title',
                        'conference_title',
                        'place',
                        'abstract',
                        'author',
                        'file.caption',
                        'file.original_file_name',
                    ];
                    const numKeys: string[] = [
                        'conference_from_year',
                        'conference_from_month',
                        'conference_from_mday',
                        'conference_to_year',
                        'conference_to_month',
                        'conference_to_mday',
                    ];
                    appendToFilter('conference', strKeys, numKeys);
                }
                if (ksType === 'all' || ksType === 'data') {
                    const strKeys: string[] = [
                        'doi',
                        'title',
                        'keyword',
                        'description',
                        'readme',
                        'rights',
                        'experimenter',
                        'data_type',
                        'file.caption',
                        'file.original_file_name',
                    ];
                    const numKeys: string[] = ['publication_year', 'publication_month', 'publication_mday'];
                    appendToFilter('data', strKeys, numKeys);
                }
                if (ksType === 'all' || ksType === 'files') {
                    const strKeys: string[] = [
                        'doi',
                        'title',
                        'keyword',
                        'description',
                        'data_file_name',
                        'data_file_mimetype',
                        'data_file_filetype',
                    ];
                    const numKeys: string[] = [];
                    appendToFilter('files', strKeys, numKeys);
                }
                if (ksType === 'all' || ksType === 'memo') {
                    const strKeys: string[] = [
                        'doi',
                        'title',
                        'keyword',
                        'description',
                        'item_link',
                        'file.original_file_name',
                    ];
                    const numKeys: string[] = [];
                    appendToFilter('memo', strKeys, numKeys);
                }
                if (ksType === 'all' || ksType === 'model') {
                    const strKeys: string[] = [
                        'doi',
                        'title',
                        'keyword',
                        'description',
                        'readme',
                        'rights',
                        'creator',
                        'model_type',
                        'file.caption',
                        'file.original_file_name',
                    ];
                    const numKeys: string[] = [];
                    appendToFilter('model', strKeys, numKeys);
                }
                if (ksType === 'all' || ksType === 'paper') {
                    const strKeys: string[] = ['doi', 'title', 'keyword', 'journal', 'page', 'pubmed_id', 'author'];
                    const numKeys: string[] = ['publication_year', 'volume', 'number'];
                    appendToFilter('paper', strKeys, numKeys);
                }
                if (ksType === 'all' || ksType === 'presentation') {
                    const strKeys: string[] = [
                        'doi',
                        'title',
                        'keyword',
                        'description',
                        'readme',
                        'rights',
                        'creator',
                        'presentation_type',
                        'file.caption',
                        'file.original_file_name',
                    ];
                    const numKeys: string[] = ['publication_year', 'publication_month', 'publication_mday'];
                    appendToFilter('presentation', strKeys, numKeys);
                }
                if (ksType === 'all' || ksType === 'simulator') {
                    const strKeys: string[] = [
                        'doi',
                        'title',
                        'keyword',
                        'description',
                        'readme',
                        'rights',
                        'developer',
                        'simulator_type',
                        'file.caption',
                        'file.original_file_name',
                    ];
                    const numKeys: string[] = ['publication_year', 'publication_month', 'publication_mday'];
                    appendToFilter('simulator', strKeys, numKeys);
                }
                if (ksType === 'all' || ksType === 'stimulus') {
                    const strKeys: string[] = [
                        'doi',
                        'title',
                        'keyword',
                        'description',
                        'readme',
                        'rights',
                        'developer',
                        'stimulus_type',
                        'file.caption',
                        'file.original_file_name',
                    ];
                    const numKeys: string[] = ['publication_year', 'publication_month', 'publication_mday'];
                    appendToFilter('stimulus', strKeys, numKeys);
                }
                if (ksType === 'all' || ksType === 'tool') {
                    const strKeys: string[] = [
                        'doi',
                        'title',
                        'keyword',
                        'description',
                        'readme',
                        'rights',
                        'developer',
                        'tool_type',
                        'file.caption',
                        'file.original_file_name',
                    ];
                    const numKeys: string[] = [];
                    appendToFilter('tool', strKeys, numKeys);
                }
                if (ksType === 'all' || ksType === 'url') {
                    const strKeys: string[] = ['doi', 'title', 'keyword', 'url', 'file.original_file_name'];
                    const numKeys: string[] = [];
                    appendToFilter('url', strKeys, numKeys);
                }
                if (ksType !== 'all') {
                    filter.item_type_name = `xnp${ksType}`;
                }
            }
            filter.$or = filterOr;
            const offset = condition.limit * (condition.page - 1);
            const result = items.chain().find(filter);
            const itemSorter = new ItemSorter(condition);
            const ret = {
                total: result.count(),
                data: result
                    .sort((a, b) => itemSorter.sort(a, b))
                    .offset(offset)
                    .limit(condition.limit)
                    .data(),
            };
            func(ret);
        });
    }

    getListByAdvancedSearchQuery(
        type: BrainAtlasType,
        query: AdvancedSearchQuery,
        condition: SortCondition,
        func: SearchCallbackFunc
    ): void {
        this.registerItemLoadCallback(type, (items) => {
            const filter: LokiSearchFilter = query.getSearchFilter();
            const offset = condition.limit * (condition.page - 1);
            const result = items.chain().find(filter);
            const itemSorter = new ItemSorter(condition);
            const ret = {
                total: result.count(),
                data: result
                    .sort((a, b) => itemSorter.sort(a, b))
                    .offset(offset)
                    .limit(condition.limit)
                    .data(),
            };
            func(ret);
        });
    }

    getSimPFLinkUrl(itemId: number) {
        const simpfLink = this.simpfLinks.findOne({ id: itemId });
        if (simpfLink === null) {
            return '';
        }
        return simpfLink.url;
    }
}

export default new ItemUtil();
